import { Injectable } from '@angular/core';
import { Log } from 'src/app/logger.service';

@Injectable({
  providedIn: 'root',
})
export class BrowserNotificationService {
  private permissionGranted = false;

  constructor() {
    // Check if browser supports notifications
    if ('Notification' in window) {
      this.permissionGranted = Notification.permission === 'granted';
    }
  }

  /**
   * Request permission to send browser notifications
   * @returns Promise that resolves to true if permission granted, false otherwise
   */
  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      Log.w('Browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      this.permissionGranted = true;
      return true;
    }

    if (Notification.permission === 'denied') {
      Log.w('Notification permission denied');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permissionGranted = permission === 'granted';
      return this.permissionGranted;
    } catch (error) {
      Log.e('Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Check if notifications are supported and permission is granted
   */
  isAvailable(): boolean {
    return 'Notification' in window && this.permissionGranted;
  }

  /**
   * Check if the page is currently hidden (in background)
   */
  isPageHidden(): boolean {
    return document.hidden;
  }

  /**
   * Send a browser notification
   * @param title Notification title
   * @param options Notification options
   */
  sendNotification(title: string, options?: NotificationOptions): Notification | null {
    if (!this.isAvailable()) {
      Log.w('Cannot send notification: permission not granted or not supported');
      return null;
    }

    try {
      const notification = new Notification(title, {
        icon: '/assets/icons/wf-icon.png',
        badge: '/assets/icons/wf-icon.png',
        ...options,
      });

      // Auto-close notification after 10 seconds
      setTimeout(() => {
        notification.close();
      }, 10000);

      return notification;
    } catch (error) {
      Log.e('Error sending notification:', error);
      return null;
    }
  }

  sendChatCompletionNotification(body: string, onClick?: () => void): void {
    const notification = this.sendNotification('Workforce.ai Response Ready', {
      body: body,
      tag: 'chat-completion',
      requireInteraction: false,
    });

    if (notification && onClick) {
      notification.onclick = () => {
        onClick();
        window.focus();
        notification.close();
      };
    }
  }
}
